import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { qk } from '../lib/keys';
import { useToast } from '../components/ui/Toast';

// Team inbox (organizations): assignment, notes and "is replying" presence.

const HEARTBEAT_MS = 15_000;

export function useAssignThread(threadId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (assigneeId: string | null) => api('POST /api/threads/:id/assign', { params: { id: threadId }, body: { assigneeId } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.thread(threadId) });
      void qc.invalidateQueries({ queryKey: qk.threadsRoot });
    },
    onError: (e) => toast.error(e),
  });
}

export function useAddNote(threadId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (body: string) => api('POST /api/threads/:id/notes', { params: { id: threadId }, body: { body } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.thread(threadId) }),
    onError: (e) => toast.error(e),
  });
}

export function useDeleteNote(threadId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (noteId: string) => api('DELETE /api/threads/:id/notes/:noteId', { params: { id: threadId, noteId } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.thread(threadId) }),
    onError: (e) => toast.error(e),
  });
}

/** While `active` (writing a reply in an organization), tell teammates every 15 s; clear it when done. */
export function useReplyPresence(threadId: string | undefined, active: boolean) {
  useEffect(() => {
    if (!threadId || !active) return;
    const beat = (replying: boolean) => void api('POST /api/threads/:id/presence', { params: { id: threadId }, body: { replying } }).catch(() => undefined);
    beat(true);
    const t = setInterval(() => beat(true), HEARTBEAT_MS);
    return () => {
      clearInterval(t);
      beat(false);
    };
  }, [threadId, active]);
}
